import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    signal,
    SimpleChanges,
    ViewChild,
    ViewChildren
} from "@angular/core";
import {CommonModule} from "@angular/common";
import {FormBuilder, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {MatButtonToggleModule} from "@angular/material/button-toggle";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {MatInputModule} from "@angular/material/input";
import {MatMenuModule} from "@angular/material/menu";
import {MatTooltipModule} from "@angular/material/tooltip";
import {Store} from "@ngrx/store";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {FinancialPerformanceRecord} from "../../../../models/asset/financial-performance-record";
import {ScenarioType} from "../../../../../shared/model/scenario-type";
import {AssetValuationDetailActions} from "../../../../store/asset.actions";
import {METRICS} from "./metric-config";
import {QUICK_FORECAST_MODE_DEFAULT, QuickForecastMetricKey, QuickForecastMode, QuickForecastSeeds} from "./quick-forecast.models";
import {QuickForecastSeedPair} from "../../../../generated-sources/api";

@Component({
    selector: "valumize-quick-forecast",
    templateUrl: "./quick-forecast.component.html",
    styleUrls: ["./quick-forecast.component.scss"],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatButtonToggleModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatMenuModule,
        MatTooltipModule
    ]
})
export class QuickForecastComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

    @Input({required: true}) assetId!: number;
    @Input({required: true}) assetValuationId!: number;
    @Input({required: true}) scenarioId!: number;
    @Input({required: true}) scenarioType!: ScenarioType;
    @Input({required: true}) earningBase!: string;
    @Input({required: true}) forecastRecords!: FinancialPerformanceRecord[];

    @Output() readonly closed = new EventEmitter<void>();

    @ViewChildren("metricInput") private readonly inputs?: QueryList<ElementRef<HTMLInputElement>>;
    @ViewChild("panel") private readonly panel?: ElementRef<HTMLElement>;

    readonly metrics = METRICS;
    readonly mode = signal<QuickForecastMode>(QUICK_FORECAST_MODE_DEFAULT);

    form!: FormGroup;

    private readonly formBuilder = inject(FormBuilder);
    private readonly store = inject(Store);
    private readonly destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.form = this.buildForm();
        this.form.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.dispatchApply());
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.form) return;
        if (changes["scenarioId"] || changes["earningBase"]) {
            this.form.reset({}, {emitEvent: false});
        }
    }

    ngAfterViewInit(): void {
        // Defer one tick so initial focus doesn't compete with the icon-button click that opened the panel.
        queueMicrotask(() => this.inputs?.first?.nativeElement.focus());
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onCloseClicked(): void {
        this.form.reset({}, {emitEvent: false});
        this.closed.emit();
    }

    onModeChange(value: QuickForecastMode): void {
        this.mode.set(value);
        this.dispatchApply();
    }

    /** Arrow keys move focus between inputs without dispatching a calc. */
    onKeyDown(event: KeyboardEvent): void {
        if (!this.inputs) return;
        const inputs = this.inputs.toArray();
        const active = document.activeElement as HTMLElement | null;
        const currentIndex = inputs.findIndex(ref => ref.nativeElement === active);
        if (currentIndex < 0) return;

        let next = currentIndex;
        switch (event.key) {
            case "ArrowDown":
            case "ArrowRight":
                next = (currentIndex + 1) % inputs.length;
                break;
            case "ArrowUp":
            case "ArrowLeft":
                next = (currentIndex - 1 + inputs.length) % inputs.length;
                break;
            default:
                return;
        }
        event.preventDefault();
        inputs[next].nativeElement.focus();
        inputs[next].nativeElement.select();
    }

    private buildForm(): FormGroup {
        const groups: Record<string, FormGroup> = {};
        for (const metric of this.metrics) {
            groups[metric.key] = this.formBuilder.group({
                firstYear: this.formBuilder.control<number | null>(
                    null,
                    {validators: metric.firstYearValidators, updateOn: "blur"}
                ),
                yoy: this.formBuilder.control<number | null>(
                    null,
                    {validators: metric.yoyValidators, updateOn: "blur"}
                )
            });
        }
        return this.formBuilder.group(groups);
    }

    private dispatchApply(): void {
        if (this.form.invalid) return;

        const seeds = this.collectSeeds();
        if (Object.keys(seeds).length === 0) {
            return;
        }

        // forecastRecords is captured here from the latest async-pipe binding. If a calc result
        // has just landed in the store but Angular's CD hasn't propagated the new Input yet, this
        // value can be one tick stale — but the effect uses switchMap, so the last dispatched call
        // always wins and the rendered state self-heals.
        this.store.dispatch(AssetValuationDetailActions.applyQuickForecast({
            assetId: this.assetId,
            assetValuationId: this.assetValuationId,
            scenarioId: this.scenarioId,
            scenarioType: this.scenarioType,
            earningBase: this.earningBase,
            forecastRecords: this.forecastRecords,
            seeds,
            mode: this.mode()
        }));
    }

    private collectSeeds(): QuickForecastSeeds {
        const seeds: QuickForecastSeeds = {};
        for (const metric of this.metrics) {
            const group = this.form.get(metric.key);
            if (!group) continue;
            const firstYear = group.get("firstYear")?.value as number | null | undefined;
            const yoy = group.get("yoy")?.value as number | null | undefined;
            if (firstYear === null || firstYear === undefined) continue;
            const seed: QuickForecastSeedPair = {firstYear, yoy: yoy ?? null};
            seeds[metric.key as QuickForecastMetricKey] = seed;
        }
        return seeds;
    }
}
